import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { alpha } from '@mui/material/styles';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import {
  getCommitments,
  createCommitment,
  updateCommitment,
  deleteCommitment,
} from './FinancialService';
import { CommitmentResponse, CommitmentRequest } from './types';

const C = {
  ink: '#17263d',
  muted: '#78869a',
  blue: '#1475d1',
  green: '#159b72',
  red: '#ff454d',
  orange: '#ff8b32',
};

const gbp = (n: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(
    n,
  );

const FREQ_OPTIONS: { value: CommitmentRequest['frequency']; label: string }[] =
  [
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'ANNUAL', label: 'Annual' },
    { value: 'IRREGULAR', label: 'Irregular' },
  ];

const FREQ_ORDER = ['MONTHLY', 'WEEKLY', 'QUARTERLY', 'ANNUAL', 'IRREGULAR'];

const monthlyEquivalent = (c: CommitmentResponse): number => {
  switch (c.frequency) {
    case 'WEEKLY':
      return (c.amount * 52) / 12;
    case 'MONTHLY':
      return c.amount;
    case 'QUARTERLY':
      return c.amount / 3;
    case 'ANNUAL':
      return c.amount / 12;
    default:
      return 0;
  }
};

function dueDateLabel(iso: string): { label: string; color: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(iso);
  due.setHours(0, 0, 0, 0);
  const diff = Math.round((due.getTime() - today.getTime()) / 86_400_000);

  if (diff < 0) return { label: `Overdue ${Math.abs(diff)}d`, color: C.red };
  if (diff === 0) return { label: 'Due today', color: C.orange };
  if (diff === 1) return { label: 'Due tomorrow', color: C.orange };
  if (diff <= 7) return { label: `Due in ${diff} days`, color: C.blue };
  return {
    label: due.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    color: C.muted,
  };
}

const todayISO = () => new Date().toISOString().slice(0, 10);

// ─── CommitmentModal ──────────────────────────────────────────────────────────

interface CommitmentModalProps {
  open: boolean;
  existing: CommitmentResponse | null;
  onClose: () => void;
  onSaved: () => void;
}

const CommitmentModal = ({
  open,
  existing,
  onClose,
  onSaved,
}: CommitmentModalProps) => {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] =
    useState<CommitmentRequest['frequency']>('MONTHLY');
  const [nextDueDate, setNextDueDate] = useState(todayISO());
  const [category, setCategory] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(existing?.name ?? '');
      setAmount(existing?.amount?.toString() ?? '');
      setFrequency(
        (existing?.frequency as CommitmentRequest['frequency']) ?? 'MONTHLY',
      );
      setNextDueDate(existing?.nextDueDate ?? todayISO());
      setCategory(existing?.category ?? '');
      setIsActive(existing?.isActive ?? true);
      setError('');
    }
  }, [open, existing]);

  const handleSave = async () => {
    const amt = parseFloat(amount);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!amt || amt <= 0) {
      setError('Enter a valid amount greater than 0.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: CommitmentRequest = {
        name: name.trim(),
        amount: amt,
        frequency,
        nextDueDate: nextDueDate || null,
        category: category || undefined,
        isActive,
      };
      if (existing) {
        await updateCommitment(existing.id, payload);
      } else {
        await createCommitment(payload);
      }
      onSaved();
      onClose();
    } catch {
      setError('Could not save commitment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {existing ? 'Edit commitment' : 'Add commitment'}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '12px !important',
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Name"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          autoFocus
        />
        <TextField
          label="Amount (£)"
          type="number"
          size="small"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">£</InputAdornment>,
          }}
          inputProps={{ min: 0, step: '0.01' }}
          fullWidth
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Frequency</InputLabel>
          <Select
            value={frequency}
            label="Frequency"
            onChange={(e) =>
              setFrequency(e.target.value as CommitmentRequest['frequency'])
            }
          >
            {FREQ_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Next due date"
          type="date"
          size="small"
          value={nextDueDate}
          onChange={(e) => setNextDueDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label="Category (optional)"
          size="small"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          fullWidth
          placeholder="e.g. Rent, Netflix, Insurance"
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" sx={{ color: C.ink }}>
            Active
          </Typography>
          <Switch
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : existing ? 'Save changes' : 'Add commitment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── DeleteCommitmentDialog ───────────────────────────────────────────────────

interface DeleteDialogProps {
  open: boolean;
  commitment: CommitmentResponse | null;
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteCommitmentDialog = ({
  open,
  commitment,
  onClose,
  onDeleted,
}: DeleteDialogProps) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (!commitment) return;
    setDeleting(true);
    setError('');
    try {
      await deleteCommitment(commitment.id);
      onDeleted();
      onClose();
    } catch {
      setError('Could not delete commitment. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete commitment?</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography>
          This will permanently remove <strong>{commitment?.name}</strong>. This
          cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={deleting}
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── CommitmentsPage ──────────────────────────────────────────────────────────

const CommitmentsPage = (): JSX.Element => {
  const [rows, setRows] = useState<CommitmentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CommitmentResponse | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CommitmentResponse | null>(
    null,
  );

  const load = () => {
    setLoading(true);
    setError('');
    getCommitments()
      .then((res) => setRows(res.data))
      .catch(() => setError('Could not load commitments.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const active = rows.filter((r) => r.isActive);
    const groups: Record<string, CommitmentResponse[]> = {};
    FREQ_ORDER.forEach((f) => {
      const members = active.filter((r) => r.frequency === f);
      if (members.length > 0) groups[f] = members;
    });
    return groups;
  }, [rows]);

  const monthlyTotal = useMemo(
    () =>
      rows
        .filter((r) => r.isActive)
        .reduce((sum, r) => sum + monthlyEquivalent(r), 0),
    [rows],
  );

  const openAdd = () => {
    setEditTarget(null);
    setModalOpen(true);
  };
  const openEdit = (c: CommitmentResponse) => {
    setEditTarget(c);
    setModalOpen(true);
  };
  const openDel = (c: CommitmentResponse) => {
    setDeleteTarget(c);
    setDeleteOpen(true);
  };

  return (
    <UserDashboard>
      <Box
        sx={{
          background: `linear-gradient(175deg, ${alpha(C.blue, 0.05)} 0%, transparent 40%)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: 3,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: C.ink }}>
                Commitments
              </Typography>
              <Typography variant="body2" sx={{ color: C.muted }}>
                Regular bills and subscriptions
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {!loading && monthlyTotal > 0 && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    variant="caption"
                    sx={{ color: C.muted, display: 'block' }}
                  >
                    Monthly equivalent
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: C.ink }}
                  >
                    {gbp(monthlyTotal)}
                  </Typography>
                </Box>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAdd}
              >
                Add commitment
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Grid container spacing={2}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Skeleton
                    variant="rectangular"
                    height={100}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : Object.keys(grouped).length === 0 ? (
            <Card>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ color: C.muted, mb: 2 }}>
                  No active commitments yet.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={openAdd}
                >
                  Add your first commitment
                </Button>
              </CardContent>
            </Card>
          ) : (
            Object.entries(grouped).map(([freq, items]) => {
              const freqLabel = freq.charAt(0) + freq.slice(1).toLowerCase();
              return (
                <Box key={freq} sx={{ mb: 4 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: C.muted,
                      fontWeight: 700,
                      mb: 1.5,
                      display: 'block',
                    }}
                  >
                    {freqLabel}
                  </Typography>
                  <Grid container spacing={2}>
                    {items.map((c) => {
                      const due = c.nextDueDate
                        ? dueDateLabel(c.nextDueDate)
                        : null;
                      return (
                        <Grid item xs={12} sm={6} md={4} key={c.id}>
                          <Card sx={{ height: '100%' }}>
                            <CardContent sx={{ p: 2.5 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                }}
                              >
                                <Box sx={{ flex: 1, mr: 1 }}>
                                  <Typography
                                    variant="body1"
                                    fontWeight={600}
                                    sx={{ color: C.ink }}
                                  >
                                    {c.name}
                                  </Typography>
                                  {c.category && (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: C.muted }}
                                    >
                                      {c.category}
                                    </Typography>
                                  )}
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    sx={{ color: C.ink, whiteSpace: 'nowrap' }}
                                  >
                                    {gbp(c.amount)}
                                  </Typography>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={() => openEdit(c)}
                                      sx={{ color: C.muted }}
                                    >
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={() => openDel(c)}
                                      sx={{ color: C.muted }}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  mt: 1.5,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Chip
                                  label={freqLabel}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    bgcolor: alpha(C.blue, 0.08),
                                    color: C.blue,
                                    fontWeight: 600,
                                  }}
                                />
                                {due && (
                                  <Chip
                                    label={due.label}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      bgcolor: alpha(due.color, 0.1),
                                      color: due.color,
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                                {c.source === 'DETECTED' && (
                                  <Chip
                                    label="Auto-detected"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      bgcolor: alpha(C.green, 0.08),
                                      color: C.green,
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              );
            })
          )}
        </Container>
      </Box>

      <CommitmentModal
        open={modalOpen}
        existing={editTarget}
        onClose={() => setModalOpen(false)}
        onSaved={load}
      />
      <DeleteCommitmentDialog
        open={deleteOpen}
        commitment={deleteTarget}
        onClose={() => setDeleteOpen(false)}
        onDeleted={load}
      />
    </UserDashboard>
  );
};

export default CommitmentsPage;
